import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  CircularProgress,
  Backdrop,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import axios from "axios";
import {
  DeleteBankAPI,
  GetAllBanksAPI,
  baseUrl,
} from "../../config/api";
import BankAdd from "./BankAdd";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import { ConfirmationDialog } from "../../components/common";
import GridItem from "../../components/Grid/GridItem";
import Card from "../../components/Card/Card";
import CardHeader from "../../components/Card/CardHeader";
import CustomInput from "../../components/CustomInput/CustomInput";
import People from "@material-ui/icons/People";
import CardBody from "../../components/Card/CardBody";
import GridContainer from "../../components/Grid/GridContainer";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import Ledger from "../Accounting/Ledger";
import LedgerList from "../Accounting/LedgerList";
import BankEdit from "./BankEdit";
import { ImportExcel } from "../Master/ImportExcel";
import { CloudDownload, CloudUpload } from "@material-ui/icons";
import * as XLSX from 'xlsx';
import Tooltip from "@material-ui/core/Tooltip";

const useStyles = makeStyles((theme) => ({
  cardCategoryWhite: {
    "&,& a,& a:hover,& a:focus": {
      color: "rgba(255,255,255,.62)",
      margin: "0",
      fontSize: "14px",
      marginTop: "0",
      marginBottom: "0",
    },
    "& a,& a:hover,& a:focus": {
      color: "#FFFFFF",
    },
  },
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "300",
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    marginBottom: "3px",
    textDecoration: "none",
    "& small": {
      color: "#777",
      fontSize: "65%",
      fontWeight: "400",
      lineHeight: "1",
    },
  },
  qrImageWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  qrImageSize: {
    maxWidth: 100,
    maxHeight: 100,
  },
}));

const columns = [
  { id: "bankName", label: "Bank Name" },
  { id: "acNo", label: "AcNo" },
  { id: "ifscCode", label: "IfscCode" },
  { id: "branch", label: "Branch" },
  { id: "qrCode", label: "QR Code" },
  { id: "action", label: "Action" },
];

export default function BankList() {
  const userInfo = useSelector((state) => state.userReducer.userInfo);
  const authToken = useSelector((state) => state.userReducer.authToken);
  const classes = useStyles();

  const [banks, setBanks] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [openLedgerSelectionDialog, setOpenLedgerSelectionDialog] = useState(false);
  const [startDate, setStartDate] = useState(new Date('2024-01-01T00:00:00'));
  const [endDate, setEndDate] = useState(new Date('2026-03-31T00:00:00'));
  const [openLedgerDialog, setOpenLedgerDialog] = useState(false);
  const [ledgerType, setLedgerType] = useState("Payment");
  const [openEdit, setOpenEdit] = useState(false);
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const hasInitialLoad = useRef(false);

  const _getBanks = useCallback(async (pageNum, rowsPerPageNum, search = "") => {
    try {
      setLoading(true);
      const skip = pageNum * rowsPerPageNum;
      let query = `&limit=${rowsPerPageNum}&skip=${skip}`;

      if (search) {
        query += `&bankName=${encodeURIComponent(search)}`;
      }

      const res = await axios.get(GetAllBanksAPI(userInfo.entity) + query, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      setBanks(res.data.banks || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error("Something went wrong, Please Contact Admin", {
        position: "top-center",
        autoClose: 1500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      setBanks([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [userInfo.entity, authToken]);

  // Initial load: only on mount (handles React.StrictMode double-invocation)
  useEffect(() => {
    if (!hasInitialLoad.current) {
      hasInitialLoad.current = true;
      _getBanks(0, rowsPerPage, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Effect for pagination changes (when page or rowsPerPage changes)
  // Note: searchTerm is used but not in deps to avoid re-running on search changes
  // (search changes are handled by search effect which resets page to 0)
  useEffect(() => {
    // Skip until initial load is complete
    if (!hasInitialLoad.current) {
      return;
    }
    _getBanks(page, rowsPerPage, searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]); // Only pagination changes trigger this

  // Debounced search effect: only runs when searchTerm changes (not on initial mount)
  useEffect(() => {
    // Skip until initial load is complete
    if (!hasInitialLoad.current) {
      return;
    }

    const timeoutId = setTimeout(() => {
      if (searchTerm) {
        // If searching, reset to page 0 and search
        setPage(0);
        _getBanks(0, rowsPerPage, searchTerm);
      } else {
        // If search cleared, reset to page 0 and fetch all
        setPage(0);
        _getBanks(0, rowsPerPage, "");
      }
    }, 300);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]); // Only searchTerm, NOT rowsPerPage (to avoid conflicts)

  // Function to refresh data (called explicitly after successful operations)
  const refreshBanks = useCallback(() => {
    _getBanks(page, rowsPerPage, searchTerm);
  }, [page, rowsPerPage, searchTerm, _getBanks]);

  async function _deleteItem(item) {
    try {
      await axios.delete(DeleteBankAPI(item._id), {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      toast.success('Successfully Deleted!', {
        position: "top-center",
        autoClose: 1500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      refreshBanks();
    } catch (e) {
      toast.error("Something went wrong, Please Contact Admin", {
        position: "top-center",
        autoClose: 1500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    }
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  function closeAddDialog() {
    setOpenAddDialog(false);
  }

  const handleOpenConfirmationModal = (object) => {
    setOpenConfirmationModal(object);
  };

  const handleCloseConfirmationModal = () => {
    setOpenConfirmationModal(null);
  };

  // Server-side search is now handled in API call
  const data = banks;

  // Export function
  const handleExport = async () => {
    setIsExporting(true);
    const loadingToastId = toast.loading('🔄 Preparing export... Please wait', {
      position: "top-center",
      autoClose: false,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: false,
      progress: undefined,
      theme: "colored",
    });

    try {
      // First, get the total count
      toast.update(loadingToastId, {
        render: '📊 Fetching total record count...',
        type: 'info',
        isLoading: true,
      });

      const totalRes = await axios.get(GetAllBanksAPI(userInfo.entity) + `&limit=1&skip=0`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      const total = totalRes.data.total || 0;
      
      if (total === 0) {
        toast.update(loadingToastId, {
          render: '❌ No data to export!',
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        });
        setIsExporting(false);
        return;
      }

      // Fetch all data using the total count
      toast.update(loadingToastId, {
        render: `📥 Downloading ${total} records... This may take a moment`,
        type: 'info',
        isLoading: true,
      });

      const res = await axios.get(GetAllBanksAPI(userInfo.entity) + `&limit=${total}&skip=0`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      const allData = res.data.banks || [];
      if (!allData || allData.length === 0) {
        toast.update(loadingToastId, {
          render: '❌ No data to export!',
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        });
        setIsExporting(false);
        return;
      }

      toast.update(loadingToastId, {
        render: '⚙️ Processing data for export...',
        type: 'info',
        isLoading: true,
      });

      const exportHeadings = [
        { id: "bankName", label: "Bank Name" },
        { id: "acNo", label: "Account Number" },
        { id: "ifscCode", label: "IFSC Code" },
        { id: "branch", label: "Branch" },
        { id: "qrCode", label: "QR Code" }
      ];

      const exportData = allData.map(row => {
        const rowData = {};
        exportHeadings.forEach(col => {
          let value = row[col.id];
          rowData[col.id] = value;
        });
        return rowData;
      });

      toast.update(loadingToastId, {
        render: '💾 Generating Excel file...',
        type: 'info',
        isLoading: true,
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Banks');
      XLSX.writeFile(workbook, 'banks_export.xlsx');

      toast.update(loadingToastId, {
        render: `✅ Successfully exported ${allData.length} records to Excel!`,
        type: 'success',
        isLoading: false,
        autoClose: 4000,
        style: {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff',
          fontWeight: 'bold',
          fontSize: '14px',
        },
      });
    } catch (error) {
      console.error('Export error:', error);
      toast.update(loadingToastId, {
        render: `❌ Export failed: ${error.response?.data?.message || error.message || 'Unknown error'}`,
        type: 'error',
        isLoading: false,
        autoClose: 5000,
        style: {
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          color: '#fff',
          fontWeight: 'bold',
          fontSize: '14px',
        },
      });
    } finally {
      setIsExporting(false);
    }
  };
  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color="primary">
            <Box
              style={{
                display: "flex",
                flex: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h4 className={classes.cardTitleWhite}>Banks</h4>
              <Box
                style={{
                  display: "flex",
                  flex: "row",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CustomInput
                  id="search"
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    style: {
                      color: "white",
                      marginTop: 0,
                    },
                    placeholder: "Search",
                    value: searchTerm,
                    onChange: (e) => setSearchTerm(e.target.value),
                    endAdornment: (
                      <InputAdornment position="end">
                        <People />
                      </InputAdornment>
                    ),
                  }}
                />
                <IconButton
                  aria-label="add"
                  style={{ color: "white" }}
                  onClick={() => setOpenAddDialog(true)}
                >
                  <AddIcon />
                </IconButton>
              </Box>
            </Box>
          </CardHeader>
          <CardBody>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={"center"}
                      style={{ minWidth: column.minWidth }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center" style={{ padding: "40px" }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center" style={{ padding: "40px" }}>
                      <Box style={{ color: "#666" }}>
                        {searchTerm ? "No banks found matching your search." : "No banks found."}
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((row) => {
                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={row._id}
                      >
                        {columns.map((column) => {
                          let value = row[column.id];
                          
                          if (column.id === "qrCode") {
                            return (
                              <TableCell key={column.id} align={"center"}>
                                {row.qrCode && row.qrCode !== "null" && row.qrCode.trim() !== "" ? (
                                  <Box className={classes.qrImageWrapper}>
                                    <img
                                      src={`${baseUrl}${row.qrCode}`}
                                      alt="QR Code"
                                      className={classes.qrImageSize}
                                      style={{ objectFit: "contain" }}
                                    />
                                  </Box>
                                ) : (
                                  "N/A"
                                )}
                              </TableCell>
                            );
                          }

                          if (column.id === "action") {
                            return (
                              <TableCell align={"center"}>
                                <Box display={"flex"} justifyContent={"center"}>
                                  <Button
                                    type="button"
                                    color="primary"
                                    onClick={() => {
                                      setSelectedItem(row);
                                      setOpenLedgerSelectionDialog(true);
                                    }}
                                  >
                                    Payment Ledger
                                  </Button>
                                  <IconButton
                                    aria-label="delete"
                                    onClick={() =>
                                      handleOpenConfirmationModal({
                                        onPress: () => _deleteItem(row),
                                      })
                                    }
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                  <IconButton
                                    aria-label="eidt"
                                    onClick={() => {
                                      setSelectedItem(row)
                                      setOpenEdit(true)
                                    }}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            );
                          }
                          return (
                            <TableCell key={column.id} align={"center"}>
                              {column.format && typeof value === "number"
                                ? column.format(value)
                                : value}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </CardBody>
        </Card>
      </GridItem>
      <BankAdd
        fetchData={refreshBanks}
        open={openAddDialog}
        close={closeAddDialog}
      />
      {openConfirmationModal ? (
        <ConfirmationDialog
          open={openConfirmationModal}
          onClose={handleCloseConfirmationModal}
          onConfirm={openConfirmationModal.onPress}
          title={"Delete Record"}
          msg={"Do you want to delete this record?"}
        />
      ) : null}
      {selectedItem && openLedgerSelectionDialog ? (
        <Ledger
          item={selectedItem}
          openLedgerSelectionDialog={openLedgerSelectionDialog}
          setOpenLedgerSelectionDialog={setOpenLedgerSelectionDialog}
          setOpenLedgerDialog={setOpenLedgerDialog}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          ledgerType={ledgerType}
          setLedgerType={setLedgerType}
          pay={true}
        />
      ) : null}

      {selectedItem && openLedgerDialog ? (
        <LedgerList
          open={openLedgerDialog}
          item={selectedItem}
          close={() => setOpenLedgerDialog(false)}
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          ledgerType={ledgerType}
        />
      ) : null}

      {openEdit && selectedItem ? (
        <BankEdit
          open={openEdit}
          item={selectedItem}
          close={() => {
            setOpenEdit(false);
            setSelectedItem(null);
          }}
          fetchData={refreshBanks}
        />
      ) : null}
      <ImportExcel
        db={"banks"}
        open={openImportDialog}
        close={() => {
          setOpenImportDialog(false);
          refreshBanks();
        }}
      />
      <Backdrop
        open={isExporting}
        style={{
          zIndex: 9999,
          color: '#fff',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress size={60} thickness={4} style={{ color: '#512da8', marginBottom: 20 }} />
          <Box style={{ fontSize: '18px', fontWeight: 'bold', marginTop: 10 }}>
            Exporting Data...
          </Box>
          <Box style={{ fontSize: '14px', marginTop: 5, opacity: 0.8 }}>
            Please wait while we prepare your Excel file
          </Box>
        </Box>
      </Backdrop>
    </GridContainer>
  );
}
